// src/pages/Admin/AdherentPret/AdherentPretPage.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import {
  getAllAdherentPrets,
  deleteAdherentPret,
} from "../../../services/adherentpretServices";
import useScrollToTop from "../../../hooks/useScollToTop";
import { ToastContainer, toast } from "react-toastify";
import Button from "../../../components/Button/Button";
import ButtonEdit from "../../../components/Button/ButtonEdit";
import ButtonDelete from "../../../components/Button/ButtonDelete";
import { FaPlus, FaSearch, FaInfoCircle } from "react-icons/fa";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import AddAdherentPretModal from "./AddAdherentPretModal";
import EditAdherentPretModal from "./EditAdherentPretModal";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";

export default function AdherentPretPage() {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [prets, setPrets] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentPret, setCurrentPret] = useState(null);

  const fetchPrets = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllAdherentPrets();
      setPrets(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors de la récupération des prêts :", error);
      toast.error("Erreur lors de la récupération des prêts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrets();
  }, [fetchPrets]);

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce prêt ?")) return;
    try {
      await deleteAdherentPret(id);
      setPrets((prev) => prev.filter((pret) => pret.id !== id));
      toast.success("Prêt supprimé avec succès");
    } catch (error) {
      console.error("Erreur lors de la suppression :", error);
      toast.error("Erreur lors de la suppression du prêt");
    }
  };

  const filteredPrets = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return prets;
    return prets.filter((pret) => {
      const fullName = `${pret?.adherent?.nom || ""} ${
        pret?.adherent?.prenom || ""
      } ${pret?.article?.nom || ""}`;
      return fullName.toLowerCase().includes(q);
    });
  }, [prets, searchTerm]);

  const isValidDate = (d) => moment(d).isValid();

  return (
    <section className="w-full">
      {/* ✅ Offset fiable pour la navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header page */}
        <HeaderTitle
          role="Administrateur"
          title="Gestion des prêts de matériels"
          align="center"
          className="mb-1"
        />

        {/* Navigation */}
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur mb-4">
          {/* Header dégradé + instructions + action */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3 text-white">
                <div className="mt-0.5 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/20">
                  <FaInfoCircle />
                </div>
                <div>
                  <p className="text-lg sm:text-xl font-semibold">
                    Instructions
                  </p>
                  <ul className="text-sm text-white/90 leading-relaxed list-disc pl-5">
                    <li>Gérez ici les prêts de matériel aux adhérents.</li>
                    <li>
                      Les articles doivent être créés dans{" "}
                      <span className="px-1 py-0.5 rounded bg-black/20">
                        Articles Admin
                      </span>
                      , non actifs, avec la catégorie{" "}
                      <span className="px-1 py-0.5 rounded bg-black/20">
                        Prêt
                      </span>
                      .
                    </li>
                    <li>
                      Utilisez la recherche pour filtrer par adhérent ou par nom
                      de matériel.
                    </li>
                  </ul>
                </div>
              </div>

              <div className="flex items-center justify-end">
                <Button
                  text="Ajouter un prêt"
                  icon={FaPlus}
                  onClick={() => setShowAddModal(true)}
                  className="!bg-white !text-slate-900 !hover:bg-white/90 !rounded-xl !shadow-md gap-1"
                />
              </div>
            </div>
          </div>

          {/* Bandeau filtre */}
          <div className="px-5 pt-5 sm:px-8">
            <div className="w-full sm:max-w-md">
              <label htmlFor="pret-search" className="sr-only">
                Rechercher par nom ou matériel
              </label>
              <div className="relative">
                <input
                  id="pret-search"
                  type="text"
                  placeholder="Rechercher par nom ou matériel…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>
            </div>
          </div>

          {/* Contenu */}
          <div className="p-4 sm:p-6">
            {/* Loading */}
            {loading && (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-xl border border-slate-200 dark:border-slate-800 p-5 bg-white/60 dark:bg-slate-900/40 h-20"
                  />
                ))}
              </div>
            )}

            {/* Empty */}
            {!loading && filteredPrets.length === 0 && (
              <div className="text-center py-14">
                <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 via-violet-500 to-pink-500 opacity-80" />
                <h3 className="mt-6 text-lg font-semibold text-slate-800 dark:text-slate-200">
                  Aucun prêt trouvé
                </h3>
                <p className="mt-1 text-slate-600 dark:text-slate-400">
                  Ajustez votre recherche ou ajoutez un nouveau prêt.
                </p>
              </div>
            )}

            {/* Table Desktop */}
            {!loading && filteredPrets.length > 0 && (
              <div className="hidden md:block">
                <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40">
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr className="bg-slate-50/80 dark:bg-slate-900/80 uppercase tracking-wide text-xs">
                          {[
                            "Adhérent",
                            "Matériel",
                            "Date prêt",
                            "Date remise",
                            "Note",
                            "Actions",
                          ].map((h) => (
                            <th
                              key={h}
                              className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-white"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
                        {filteredPrets.map((pret) => {
                          const datePret = isValidDate(pret?.date_pret)
                            ? moment(pret.date_pret).format("DD/MM/YY")
                            : "—";
                          const dateRemiseValid = isValidDate(
                            pret?.date_remise
                          );
                          const dateRemise = dateRemiseValid
                            ? moment(pret.date_remise).format("DD/MM/YY")
                            : null;

                          return (
                            <tr
                              key={pret.id}
                              className="group transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                            >
                              <td className="px-4 py-3 text-slate-800 dark:text-slate-100">
                                {pret?.adherent?.nom} {pret?.adherent?.prenom}
                              </td>
                              <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                                {pret?.article?.nom || "—"}
                              </td>
                              <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                                {datePret}
                              </td>
                              <td className="px-4 py-3">
                                {dateRemise ? (
                                  <span className="text-slate-700 dark:text-slate-300">
                                    {dateRemise}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center rounded-md bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 px-2 py-0.5 text-xs font-semibold">
                                    En cours
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                                {pret?.note_pret || "—"}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <ButtonEdit
                                    onClick={() => {
                                      setCurrentPret(pret);
                                      setShowEditModal(true);
                                    }}
                                  />
                                  <ButtonDelete
                                    onClick={() => handleDelete(pret.id)}
                                  />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Cartes Mobile */}
            {!loading && filteredPrets.length > 0 && (
              <div className="md:hidden space-y-3">
                {filteredPrets.map((pret) => {
                  const datePret = isValidDate(pret?.date_pret)
                    ? moment(pret.date_pret).format("DD/MM/YY")
                    : "—";
                  const dateRemiseValid = isValidDate(pret?.date_remise);
                  const dateRemise = dateRemiseValid
                    ? moment(pret.date_remise).format("DD/MM/YY")
                    : null;

                  return (
                    <div
                      key={`card-${pret.id}`}
                      className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 p-4 shadow-sm transition hover:shadow-md"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 truncate">
                            {pret?.adherent?.prenom} {pret?.adherent?.nom}
                          </h3>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            {pret?.article?.nom || "—"}
                          </p>
                        </div>
                        <div className="inline-flex items-center rounded-md border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/40 px-2 py-0.5 text-sm font-semibold text-slate-800 dark:text-slate-100">
                          {dateRemise ? "Terminé" : "En cours"}
                        </div>
                      </div>

                      <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                        <div className="rounded-lg bg-slate-50 dark:bg-slate-800/40 p-2">
                          <div className="text-[11px] text-slate-500 dark:text-slate-400">
                            Date prêt
                          </div>
                          <div className="font-medium text-slate-800 dark:text-slate-100">
                            {datePret}
                          </div>
                        </div>
                        <div className="rounded-lg bg-slate-50 dark:bg-slate-800/40 p-2">
                          <div className="text-[11px] text-slate-500 dark:text-slate-400">
                            Date remise
                          </div>
                          <div className="font-medium">
                            {dateRemise ? (
                              <span className="text-slate-800 dark:text-slate-100">
                                {dateRemise}
                              </span>
                            ) : (
                              <span className="inline-flex items-center rounded-md bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 px-2 py-0.5 text-xs font-semibold">
                                En cours
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {!!pret?.note_pret && (
                        <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">
                          {pret.note_pret}
                        </p>
                      )}

                      <div className="mt-3 flex items-center gap-2">
                        <ButtonEdit
                          onClick={() => {
                            setCurrentPret(pret);
                            setShowEditModal(true);
                          }}
                        />
                        <ButtonDelete onClick={() => handleDelete(pret.id)} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <ToastContainer />
      </div>

      {/* Modales */}
      {showAddModal && (
        <AddAdherentPretModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          refreshList={fetchPrets}
        />
      )}

      {showEditModal && currentPret && (
        <EditAdherentPretModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          pret={currentPret}
          refreshList={fetchPrets}
        />
      )}
    </section>
  );
}
